import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Send,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_agency-square/artifacts/7zqhhxkv_squre%20trigger%20black%20%282%29.png";

const serviceLinks = [
  { name: "Website Development", path: "/services/website-development" },
  { name: "SEO Services", path: "/services/seo-services" },
  { name: "Social Media Marketing", path: "/services/social-media-marketing" },
  { name: "Custom Tool Creation", path: "/services/custom-tool-creation" },
  { name: "Maintenance & Support", path: "/services/maintenance-support" },
];

const companyLinks = [
  { name: "About Us", path: "/about" },
  { name: "Contact", path: "/contact" },
];

const resourceLinks = [
  { name: "Blog", path: "/blog" },
  { name: "FAQs", path: "/resources/faqs" },
];

const legalLinks = [
  { name: "Privacy Policy", path: "/privacy-policy" },
  { name: "Terms of Service", path: "/terms-of-service" },
];

const socialLinks = [
  {
    name: "Facebook",
    icon: Facebook,
    url: "https://www.facebook.com/profile.php?id=61585572042270",
  },
  {
    name: "Twitter",
    icon: Twitter,
    url: "",
  },
  {
    name: "Instagram",
    icon: Instagram,
    url: "https://www.instagram.com/square_trigger.official/",
  },
  {
    name: "LinkedIn",
    icon: Linkedin,
    url: "https://www.linkedin.com/company/square-trigger/",
  },
  {
    name: "YouTube",
    icon: Youtube,
    url: "#",
  },
];

export const Footer = () => {
  const [email, setEmail] = React.useState("");
  const navigate = useNavigate();

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert("Thank you for subscribing!");
    setEmail("");
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleInternalNavigation = (e, path) => {
    e.preventDefault();
    navigate(path);
    scrollToTop();
  };

  return (
    <footer className="bg-foreground text-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-72 h-72 md:w-96 md:h-96 rounded-full bg-primary blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 md:w-96 md:h-96 rounded-full bg-secondary blur-3xl" />
      </div>

      <div className="container-custom relative z-20 pointer-events-auto">
        {/* Newsletter Section */}
        <div className="border-b border-background/10 py-12 md:py-16">
          <div className="max-w-2xl mx-auto text-center">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-display font-bold mb-4"
            >
              Stay Updated With Digital Insights
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-background/70 mb-8 px-2"
            >
              Get useful updates on website development, SEO, digital strategy,
              and business growth.
            </motion.p>

            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/10 border-background/20 text-background placeholder:text-background/50 focus:border-primary h-11"
                required
              />
              <Button variant="gradient" type="submit" className="shrink-0 h-11">
                Subscribe
                <Send className="h-4 w-4 ml-2" />
              </Button>
            </motion.form>
          </div>
        </div>

        {/* Main Footer */}
        <div className="py-12 md:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 md:gap-10 lg:gap-12 items-start">
          {/* Brand */}
          <div className="lg:col-span-2 flex flex-col items-center md:items-start text-center md:text-left">
            <Link
              to="/"
              onClick={(e) => handleInternalNavigation(e, "/")}
              className="inline-block mb-6 relative z-20"
            >
              <img
                src={LOGO_URL}
                alt="Square Trigger"
                className="h-12 md:h-14 w-auto object-contain brightness-0 invert mx-auto md:mx-0"
              />
            </Link>

            <div className="mb-3">
              <h4 className="font-display font-semibold text-xl text-background">
                Square Trigger
              </h4>
              <p className="text-background/70 text-sm mt-1">
                Digital Agency in Vancouver, BC
              </p>
            </div>

            <p className="text-background/70 mb-6 max-w-sm leading-relaxed">
              Website development, SEO services, social media marketing, custom
              tool creation, and ongoing support for businesses that want a
              stronger online presence.
            </p>

            {/* Contact Info */}
            <div className="space-y-4 w-full flex flex-col items-center md:items-start">
              <a
                href="mailto:info@squaretrigger.com"
                className="relative z-20 w-full flex justify-center md:justify-start text-background/70 hover:text-background transition-colors"
              >
                <span className="inline-flex items-center gap-3 max-w-fit text-center md:text-left">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="break-all">info@squaretrigger.com</span>
                </span>
              </a>

              <a
                href="tel:236869335"
                className="relative z-20 w-full flex justify-center md:justify-start text-background/70 hover:text-background transition-colors"
              >
                <span className="inline-flex items-center gap-3 max-w-fit text-center md:text-left">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>236-869-335</span>
                </span>
              </a>

              <div className="w-full flex justify-center md:justify-start text-background/70">
                <div className="inline-flex items-start gap-3 max-w-[290px] md:max-w-sm text-left">
                  <MapPin className="h-4 w-4 mt-1 shrink-0" />
                  <span className="leading-relaxed">Vancouver, BC</span>
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
            <h4 className="font-display font-semibold text-xl mb-6 text-background">
              Services
            </h4>

            <ul className="space-y-4">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={(e) => handleInternalNavigation(e, link.path)}
                    className="relative z-20 text-background/70 hover:text-background transition-colors inline-block py-1 px-2 lg:px-0 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
            <h4 className="font-display font-semibold text-xl mb-6 text-background">
              Company
            </h4>

            <ul className="space-y-4">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={(e) => handleInternalNavigation(e, link.path)}
                    className="relative z-20 text-background/70 hover:text-background transition-colors inline-block py-1 px-2 lg:px-0 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources + Legal */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
            <h4 className="font-display font-semibold text-xl mb-6 text-background">
              Resources
            </h4>

            <ul className="space-y-4 mb-10">
              {resourceLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={(e) => handleInternalNavigation(e, link.path)}
                    className="relative z-20 text-background/70 hover:text-background transition-colors inline-block py-1 px-2 lg:px-0 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="font-display font-semibold text-xl mb-6 text-background">
              Legal
            </h4>

            <ul className="space-y-4">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={(e) => handleInternalNavigation(e, link.path)}
                    className="relative z-20 text-background/70 hover:text-background transition-colors inline-block py-1 px-2 lg:px-0 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/10 py-8">
          <div className="flex flex-col items-center justify-center md:flex-row md:justify-between gap-6 text-center">
            <div className="text-background/60 text-sm">
              © {new Date().getFullYear()} Square Trigger. All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {socialLinks.map((social) => {
                const Icon = social.icon;

                if (!social.url) return null;

                return (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative z-20 p-3 rounded-lg bg-background/10 text-background/70 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.name}
                  >
                    <Icon className="h-4 w-4" />
                  </motion.a>
                );
              })}
            </div>

            <div className="text-background/60 text-sm">
              Built for businesses that want a stronger online presence.
            </div>
          </div>
        </div>
      </div>

      {/* Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Square Trigger",
          description:
            "Website development, SEO services, social media marketing, custom tool creation, and ongoing support for businesses that want a stronger online presence.",
          url: "https://squaretrigger.com",
          logo: LOGO_URL,
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "236-869-335",
            contactType: "customer service",
            email: "info@squaretrigger.com",
          },
          sameAs: socialLinks
            .filter((item) => item.url && item.url !== "#")
            .map((item) => item.url),
        })}
      </script>
    </footer>
  );
};